/**
 * LegalPage — renders /privacy and /terms.
 *
 * Both pages are dynamic content stored in `site_content`:
 *   privacy → legal_privacy_title + legal_privacy_body (markdown)
 *   terms   → legal_terms_title   + legal_terms_body   (markdown)
 *
 * Admin edits these from /admin/legal-pages.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../lib/api";
import { getCachedSiteContent } from "../lib/useSiteContent";
import { OuterNav } from "../layouts/PublicNav";
import SeoPageHead from "../components/SeoPageHead";
import { formatDate } from "../lib/date";

const PAGE_CONFIG = {
  privacy: {
    titleKey: "legal_privacy_title",
    bodyKey: "legal_privacy_body",
    defaultTitle: "Privacy Notice",
    defaultBody: "_Privacy notice not yet published. Admin can edit this from /admin/legal-pages._",
  },
  terms: {
    titleKey: "legal_terms_title",
    bodyKey: "legal_terms_body",
    defaultTitle: "Terms of Service",
    defaultBody: "_Terms not yet published. Admin can edit this from /admin/legal-pages._",
  },
  cookies: {
    titleKey: "legal_cookies_title",
    bodyKey: "legal_cookies_body",
    defaultTitle: "Cookie Policy",
    defaultBody: "_Cookie policy not yet published. Admin can edit this from /admin/legal-pages._",
  },
};

// Custom markdown element styling — gives us magazine-quality typography
// without depending on the @tailwindcss/typography plugin.
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h2 className="text-3xl md:text-4xl font-black tracking-tighter mt-12 mb-4 leading-tight" {...p} />,
  h2: ({ node, ...p }) => <h2 className="text-2xl md:text-3xl font-black tracking-tight mt-12 mb-4 leading-tight" {...p} />,
  h3: ({ node, ...p }) => <h3 className="text-xl md:text-2xl font-bold tracking-tight mt-8 mb-3" {...p} />,
  h4: ({ node, ...p }) => <h4 className="text-lg font-bold mt-6 mb-2" {...p} />,
  p:  ({ node, ...p }) => <p className="my-4 text-base md:text-lg leading-relaxed text-neutral-800" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-6 my-4 space-y-2 text-base md:text-lg leading-relaxed" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-6 my-4 space-y-2 text-base md:text-lg leading-relaxed" {...p} />,
  li: ({ node, ...p }) => <li className="text-neutral-800" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-mango pl-5 py-1 my-6 italic text-neutral-700" {...p} />,
  a:  ({ node, ...p }) => <a className="text-black underline underline-offset-2 hover:no-underline font-bold" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold text-black" {...p} />,
  hr: () => <hr className="my-10 border-t-2 border-neutral-200" />,
  code: ({ node, inline, ...p }) =>
    inline ? <code className="bg-neutral-100 px-1.5 py-0.5 text-sm font-mono" {...p} /> : <code {...p} />,
};

export default function LegalPage({ kind }) {
  const cfg = PAGE_CONFIG[kind] || PAGE_CONFIG.privacy;
  const [content, setContent] = useState(getCachedSiteContent);
  const loc = useLocation();
  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data || {})).catch(() => {});
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  const title = content[cfg.titleKey] || cfg.defaultTitle;
  const body = content[cfg.bodyKey] || cfg.defaultBody;
  const updatedAt = content[`${cfg.titleKey}_updated_at`];

  return (
    <div className="min-h-screen bg-white">
      <SeoPageHead
        pageKey={kind}
        path={`/${kind}`}
        defaultTitle={`${cfg.defaultTitle} | oodel.`}
        defaultDescription={kind === "privacy"
          ? "How oodel. collects, stores and uses panelist data. Plain English, no dark patterns."
          : "The rules of the road for panelists and customers using oodel. — transparent, balanced, and Pakistani-law-aligned."}
      />
      <OuterNav />
      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="overline mb-3 text-mango">LEGAL</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 leading-[1.05]" data-testid="legal-title">{title}</h1>
        <div className="text-xs md:text-sm text-neutral-500 font-mono border-b border-neutral-200 pb-4 mb-10">
          Last updated: {updatedAt ? formatDate(updatedAt) : "—"}
        </div>
        <article data-testid="legal-body" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{body}</ReactMarkdown>
        </article>
      </main>
    </div>
  );
}
